// src/components/proposals/foundationalBlocks.js
//
// Foundational Upgrade Plan blocks: named groups of priced line items shown on
// the proposal as separately-quoted projects.
//
// Kept deliberately separate from "Your IT Investment". The original design
// badges every one of these cards "Quoted Separately", so a block subtotal must
// never roll into the monthly or once-off totals.

export const newBlockId = () =>
  `fb-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
export const newLineItemId = () =>
  `li-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/* Line item shape mirrors the Quote line item so a block can later be turned
   into a real Quote without reshaping anything. Freight fields are deliberately
   absent — Foundational pricing is indicative and carries no freight. */
export function makeFoundationalLineItem(patch = {}) {
  return {
    id: newLineItemId(),
    description: "",     // short header shown prominently on the document
    detail: "",          // optional longer text, rendered under the header
    quantity: 1,
    unitCost: 0,
    unitPrice: 0,
    /* ⚠ CONFIRMED, unlike a backfilled one. A person is choosing this item's
       category as they type it, right there in the form — there is nothing to
       review afterwards. Only the backfill's guesses arrive unconfirmed. */
    /* The literal rather than an import: lineItemCategory imports lineItemTotal
       from THIS file so the money maths has one definition, and importing back
       the other way would be a cycle. DEFAULT_CATEGORY there must agree. */
    category: "hardware",
    categoryConfirmed: true,
    sku: "",
    manufacturer: "",
    supplier: "",
    supplierSku: "",
    stockNote: "",
    imageUrl: "",
    ...patch,
  };
}

export function makeFoundationalBlock(patch = {}) {
  return {
    id: newBlockId(),
    title: "",
    description: "",
    timeline: "",
    lineItems: [],
    ...patch,
  };
}

/* Sell price from cost. Quantity is applied at the row, never here. */
export function priceFromCost(unitCost, markupPct) {
  const cost = Number(unitCost) || 0;
  const pct = Number(markupPct) || 0;
  return Math.round(cost * (1 + pct / 100) * 100) / 100;
}

/* ⛔ ABSENT IS NOT ZERO, AND `||` CANNOT TELL THEM APART — which is what let
   the proposal and the quote disagree about the same line for months.
   `foundationalBlocks` fell back to 1 and every quote path fell back to 0, so a
   block line whose quantity was missing was worth FULL PRICE on the proposal and
   NOTHING on the quote Stage D generated from it. Worse, `toQuoteLineItem` did
   not merely render the 0 — it WROTE it into the stored quote, so the loss
   outlived the fallback that caused it.

   Neither old fallback was right on its own:
     `|| 1`  charges for one unit when the user explicitly typed 0
     `|| 0`  makes a line free when the quantity is simply absent

   So absence and zero are now separated. An ABSENT quantity (undefined, null, or
   the empty string the builder holds mid-edit while the field is cleared) takes
   the 1 that every line-item factory in both repos already seeds. An EXPLICIT 0
   stays 0, because the user meant it. Unparseable text falls back to the same
   factory default rather than silently zeroing a priced line.

   ⚠ THE BACKEND CARRIES ITS OWN COPY of this rule in quoteRenderer.js — separate
   repo, no shared module — and tests/quoteRenderer.test.js pins the two against
   each other. Change this and that test fails until the port lands, which is the
   point. */
export function quantityOf(li) {
  const raw = li && li.quantity;
  if (raw === "" || raw === null || raw === undefined) return 1;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 1;
}

export function lineItemTotal(li) {
  return (Number(li?.unitPrice) || 0) * quantityOf(li);
}

/* A block's own subtotal. There is deliberately no grand total across blocks —
   each is quoted separately, and inventing a combined figure would read as a
   single committed price. */
export function computeBlockSubtotal(block) {
  return (block?.lineItems || []).reduce((sum, li) => sum + lineItemTotal(li), 0);
}

export const fmtMoney = (n) =>
  "$" + Number(n || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

/* Normalise one AI suggestion into a line item. The suggest endpoint is shared
   with Quoting and returns routing hints (targetSectionId / targetOptionLabel)
   that mean nothing here — a flat block has no sections — so they are dropped
   rather than carried as dead data. */
export function lineItemFromSuggestion(s, markupPct) {
  const unitCost = Number(s?.unitCost) || 0;
  const unitPrice = Number(s?.unitPrice) || priceFromCost(unitCost, markupPct);
  return makeFoundationalLineItem({
    description: s?.description || "",
    detail: s?.detail || "",
    quantity: Number(s?.quantity) || 1,
    unitCost,
    unitPrice,
    sku: s?.sku || "",
    manufacturer: s?.manufacturer || "",
    supplier: s?.supplier || "",
    supplierSku: s?.supplierSku || s?.sku || "",
    stockNote: s?.stockNote || "",
    imageUrl: s?.imageUrl || "",
  });
}

/* ══════════════════════════════════════════════════════════════════════
   Pagination — Foundational is the only section with unbounded content

   Every other page in these templates is a fixed 794x1123 div rendered as
   exactly one Prince page. Foundational keeps that pattern: rather than letting
   one tall container fragment, blocks are packed into as many fixed-height page
   divs as they need, each with its own header bar.

   Heights are ESTIMATED from the data, deliberately, not measured in the DOM.
   The print path is renderToString — a static string with no layout pass — so
   nothing can be measured there. Any DOM measurement would have to be taken in
   the browser and carried across, leaving the preview and the PDF running two
   separate calculations that must agree. A pure function called by both cannot
   disagree with itself, and that guarantee is worth more here than the extra
   precision, especially since browser measurements are not authoritative for
   Prince anyway.

   Estimates round UP throughout, and SAFETY trims the usable height, so the
   failure direction is a page that is slightly under-filled rather than one
   that clips. Clipping is silent and reaches the customer; a little whitespace
   does not.
   ══════════════════════════════════════════════════════════════════════ */

const PAGE_H        = 1123;
const HEADER_H      = 48;
const FOOTER_H      = 36;
const PAD_TOP       = 44;
const PAD_BOTTOM    = 52;
/* Section label + heading + intro paragraph, first page only. */
export const FOUNDATIONAL_INTRO_H = 114;
/* The "(continued)" line on later pages. */
export const FOUNDATIONAL_CONT_H = 31;
const SAFETY = 0.96;

/* The estimate-only disclaimer, rendered under the intro on the FIRST page of
   the section only — same rule as the section title.

   Defined here rather than three times in the templates because the three tier
   documents can end up in front of one prospect, and two different wordings of
   a pricing caveat is the kind of difference a customer notices and we then
   have to explain. Each tier still owns an editable copy in its
   defaultContent; this is only the seed. */
export const DEFAULT_FOUNDATIONAL_PRICING_NOTE =
  "Pricing shown for Foundational Upgrade items is indicative only. Hardware " +
  "and software costs can fluctuate over short periods — final pricing will be " +
  "confirmed at the time of order.";

export const FOUNDATIONAL_CONTENT_H =
  Math.floor((PAGE_H - HEADER_H - FOOTER_H - PAD_TOP - PAD_BOTTOM) * SAFETY);

const lines = (text, charsPerLine) =>
  Math.max(1, Math.ceil((String(text || "").length || 1) / charsPerLine));

/* Height the pricing note adds to the first page, 0 when it is empty.

   A CONSTANT would have been wrong: the note is an editable field, so a user
   who writes three sentences would push a block past the bottom of a page that
   still believed it had room — and .page is overflow:hidden, so that block
   would vanish rather than reflow. Estimated from the text for the same reason
   every other height here is: the print path is renderToString and has no
   layout pass to measure. 100 chars/line is conservative for 11.5px across the
   666px content width; rounding up keeps the failure direction as whitespace. */
export function estimateFoundationalNoteH(note) {
  const text = String(note || "").trim();
  if (!text) return 0;
  return lines(text, 100) * 18 + 16;              // 11.5px x 1.5 line-height, + margin
}

/* The card's parts, measured separately so a block can be split BETWEEN them.
   Every value is the same one estimateBlockHeight always used; they are only
   named individually now so the packer can reason about them. */
export const FB_H = {
  chrome:      34,   // border + 16px padding, top and bottom
  badgeRow:    38,   // number badge + QUOTED SEPARATELY pill, plus its margin
  contHeader:  30,   // the "01 (cont.) — Title" line on a continuation card
  colHead:     21,   // ITEM / QTY / PRICE / TOTAL
  subtotal:    25,
  gap:         12,   // to the next card
  contFooter:  15,   // "continued on next page →"
};

/* One ATOMIC unit: the item's row and its wrapped detail paragraph, measured
   together because they must never be separated by a page break. */
export function itemUnitHeight(li) {
  let h = lines(li?.description, 62) * 19;
  if (li?.detail) h += lines(li.detail, 68) * 16 + 2;
  return h;
}

/* The full header of a block's FIRST fragment: badge row, title, and the
   optional description and timeline. */
export function blockHeaderHeight(block) {
  let h = FB_H.badgeRow;
  h += lines(block?.title, 75) * 18 + 6;        // 13.5px semibold heading
  if (block?.description) h += lines(block.description, 95) * 20 + 8;
  if (block?.timeline)    h += lines(block.timeline, 100) * 16 + 8;
  return h;
}

/* Height a rendered block card occupies, including the gap beneath it.
   Composed from the parts above so the whole can never disagree with the sum
   of what the packer places. */
export function estimateBlockHeight(block) {
  const items = Array.isArray(block?.lineItems) ? block.lineItems : [];
  let h = FB_H.chrome + blockHeaderHeight(block);
  if (items.length) h += FB_H.colHead + items.reduce((s, li) => s + itemUnitHeight(li), 0);
  h += FB_H.subtotal + FB_H.gap;
  return Math.ceil(h);
}

/* Pack blocks into pages as FRAGMENTS, splitting a long block at item
   boundaries rather than letting it overflow.
 *
 * ⚠ What this replaces, and why. The old rule was "a block never splits" — a
 * block taller than a page got a page to itself and was ALLOWED TO OVERFLOW.
 * `.page` is overflow:hidden, so Prince did not reflow that overflow, it CLIPPED
 * it: the last items and the whole subtotal disappeared, the card border ran off
 * the bottom of the page with no closing edge, and the next page began the NEXT
 * block with nothing marking the loss. A customer reading it could not tell that
 * anything was missing, which is what made it worth fixing properly.
 *
 * Each returned fragment is a self-contained card:
 *   { block, num, items, isFirst, isLast, continuesBelow }
 * `isFirst` carries the real header; a continuation carries "NN (cont.) — Title"
 * and repeats the column header; `isLast` carries the subtotal.
 *
 * The rules, in the order they are applied:
 *   • a header is never stranded — it is placed only if at least one item can
 *     follow it on the same page;
 *   • an item unit is never split from its own detail paragraph;
 *   • the subtotal is never a widow — the fragment carrying it always carries at
 *     least one item too;
 *   • a NEW block will not start at the bottom of a page unless at least
 *     ORPHAN_MIN of its items fit there, so a block cannot open with a lonely
 *     row. A block too tall for any page is exempt, or it could never start.
 */
export const ORPHAN_MIN = 2;

export function chunkFoundationalBlocks(blocks, pricingNote = "") {
  const list = Array.isArray(blocks) ? blocks : [];
  if (!list.length) return [[]];                // one empty page keeps the section visible

  const cap = FOUNDATIONAL_CONTENT_H;
  const pages = [];
  let page = [];
  let used = FOUNDATIONAL_INTRO_H + estimateFoundationalNoteH(pricingNote);

  const breakPage = () => { pages.push(page); page = []; used = FOUNDATIONAL_CONT_H; };
  const room = () => cap - used;

  list.forEach((block, bi) => {
    const num = bi + 1;
    const items = Array.isArray(block?.lineItems) ? block.lineItems : [];
    const unit = items.map(itemUnitHeight);
    const wholeH = estimateBlockHeight(block);
    const tallerThanAnyPage = wholeH > cap - FOUNDATIONAL_CONT_H;

    let idx = 0;                                 // next item to place
    let isFirst = true;

    /* Orphan control, before the block starts. A block that cannot fit its
       header plus ORPHAN_MIN items in what is left begins on the next page —
       unless it is taller than a page, where waiting would gain nothing. */
    if (page.length && !tallerThanAnyPage) {
      const openingH = FB_H.chrome + blockHeaderHeight(block)
        + (items.length ? FB_H.colHead : 0)
        + unit.slice(0, Math.min(ORPHAN_MIN, unit.length)).reduce((a, b) => a + b, 0)
        + (items.length <= ORPHAN_MIN ? FB_H.subtotal + FB_H.gap : 0);
      if (openingH > room()) breakPage();
    }

    /* eslint-disable no-constant-condition */
    while (true) {
      const headH = FB_H.chrome
        + (isFirst ? blockHeaderHeight(block) : FB_H.contHeader)
        + (items.length ? FB_H.colHead : 0);

      // An empty block is a header and a subtotal; it cannot be split.
      if (!items.length) {
        const h = headH + FB_H.subtotal + FB_H.gap;
        if (page.length && h > room()) breakPage();
        page.push({ block, num, items: [], isFirst: true, isLast: true, continuesBelow: false });
        used += h;
        return;
      }

      // Never strand a header: it needs room for itself and the next item.
      if (page.length && headH + unit[idx] > room()) breakPage();

      /* Take whole items while they fit, RESERVING the fragment's closing
         element as we go. At this point we cannot know whether this fragment
         ends the block, so we reserve the smaller of the two — the continuation
         footer — and check the larger (subtotal + gap) once we know. Reserving
         nothing here is what let a page be charged more than it had budgeted. */
      let avail = room() - headH - FB_H.contFooter;
      let take = 0;
      while (idx + take < items.length && unit[idx + take] <= avail) {
        avail -= unit[idx + take];
        take += 1;
      }

      /* Nothing fits even on a fresh page — the single item is taller than a
         page. Place it alone and move on; splitting a row's own text is beyond
         what this packer does, and the pre-flight still reports the overflow. */
      if (take === 0) take = 1;

      let isLast = idx + take >= items.length;

      /* Widow rule: the subtotal shares the fragment with its items. Closing
         the block costs more than continuing it, so re-check against that
         larger figure; if it no longer fits, hand the last item down to keep
         the subtotal company — unless that would strand the header. */
      const closingShortfall = (FB_H.subtotal + FB_H.gap) - (avail + FB_H.contFooter);
      if (isLast && closingShortfall > 0) {
        if (take > 1) { take -= 1; isLast = false; }
        else if (page.length) { breakPage(); continue; }
      }

      page.push({ block, num, items: items.slice(idx, idx + take),
                  isFirst, isLast, continuesBelow: !isLast });
      used += headH + unit.slice(idx, idx + take).reduce((a, b) => a + b, 0)
            + (isLast ? FB_H.subtotal + FB_H.gap : FB_H.contFooter);

      idx += take;
      isFirst = false;
      if (isLast) break;
      breakPage();
    }
  });

  if (page.length) pages.push(page);
  return pages;
}
